<template>
    <div class="project-basics">
        <div class="row m-0">
            <!--project_name  -->
            <div class="col-md-12 mt-2 p-0">
                <form-input
                    type="text"
                    :errors="v$.project_name"
                    v-model="project.project_name"
                    id="project_name"
                    :label="
                        $t('fields.one_line_project_summery_title_or_headline')
                    "
                    :required="true"
                    :placeholder="$t('placeholders.one_line_project_summary')"
                    :tooltipText="
                        $t(
                            'tooltips.In one line state what we did, why we did and for what end outcome'
                        )
                    "
                >
                </form-input>
            </div>
            <!--business_context_background  -->
            <div class="col-md-12 mt-2 p-0">
                <form-tiptap-text-area
                    rows="5"
                    :errors="v$.business_context_background"
                    v-model="project.business_context_background"
                    id="business_context_background"
                    :label="$t('fields.business_context_background')"
                    :required="true"
                    :showToolbar="true"
                    :placeholder="
                        $t('placeholders.business_context_background')
                    "
                    :tooltipText="
                        $t(
                            'tooltips.Eg Saudi Arabia’s cultural sector is undergoing rapid growth, driven by Vision 2030 goals'
                        )
                    "
                />
            </div>

            <!--client_challenge  -->
            <div class="col-md-12 mt-2 p-0">
                <form-tiptap-text-area
                    type="text"
                    :errors="v$.client_challenge"
                    v-model="project.client_challenge"
                    id="client_challenge"
                    :label="$t('fields.client_challenge')"
                    :required="true"
                    :placeholder="$t('placeholders.client_challenge')"
                    :tooltipText="
                        $t(
                            'tooltips.Eg, Declining youth participation in cultural programs'
                        )
                    "
                />
            </div>
            <!--tam_mandate  -->
            <div class="col-md-12 mt-2 p-0">
                <form-tiptap-text-area
                    rows="5"
                    :errors="v$.tam_mandate"
                    v-model="project.tam_mandate"
                    id="tam_mandate"
                    :label="$t('fields.tam_mandate')"
                    :required="true"
                    :placeholder="$t('placeholders.tam_mandate')"
                />
            </div>
            <!--expected_results_impact  -->
            <div class="col-md-12 mt-2 p-0">
                <form-tiptap-text-area
                    rows="5"
                    :errors="v$.expected_results_impact"
                    v-model="project.expected_results_impact"
                    id="expected_results_impact"
                    :label="$t('fields.expected_results_impact')"
                    :required="true"
                    :placeholder="$t('placeholders.expected_results_impact')"
                />
            </div>

            <!-- description (edit mode only) -->
            <div
                class="col-md-12 mt-2 p-0"
                v-if="isEditMode && project.description?.length > 0"
            >
                <form-tiptap-text-area
                    rows="5"
                    v-model="project.description"
                    id="description"
                    :label="$t('fields.description')"
                    :required="false"
                    :placeholder="$t('placeholders.description')"
                />
            </div>

            <!-- Tags -->
            <div class="col-md-12 mt-2 p-0">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.tags')"
                    :model="project"
                    :multiple="true"
                    :options="tagsForSelect"
                    :required="false"
                    :allow-create="true"
                    :on-create-tag="createTag"
                    clearable
                    label-field="name"
                    name="tags"
                    :placeholder="$t('placeholders.tags')"
                    @change="handleTagChange"
                />
                <small
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                    v-if="tagLimitError"
                >
                    {{ tagLimitError }}
                </small>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import TagApi from "@api/tag.api";
import FormElSelect from "@/components/form/FormElSelect.vue";
import { useMetadataStore } from "@store/metadata";

const state = useMetadataStore();
const metadata = computed(() => state.getMetaData);

const isEditMode = computed(() => !!(props?.project && props.project.id));

// Get tags from metadata store
const tagsFromMeta = computed(() => metadata.value.tags || []);

// Convert tags to the same format as usersForSelect
const tagsForSelect = computed(() => {
    return tagsFromMeta.value
        .filter((tag) => tag && tag.name && typeof tag.name === "string")
        .map((tag) => ({
            id: tag.id,
            name: tag.name,
        }));
});

const props = defineProps({
    project: {
        type: Object,
        default: null,
    },
    v$: {
        type: Object,
        default: null,
    },
    formErrors: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(["update:modelValue"]);

const localData = ref({ ...props.modelValue });

watch(
    localData,
    () => {
        emit("update:modelValue", localData.value);
    },
    { deep: true }
);

const tagLimitError = ref("");

// Simple tag creation function for form-el-select
const createTag = async (tagName) => {
    try {
        // Create the tag in the database (firstOrCreate handles duplicates)
        const response = await TagApi.store({ name: tagName });

        // Add the new tag to the metadata store
        const newTag = {
            id: response.data.data.tag.id,
            name: response.data.data.tag.name,
        };

        // Ensure the newly created tag is selected by default
        if (!Array.isArray(props.project.tags)) {
            props.project.tags = [];
        }

        // If a temporary label was added by the select, replace it with the new ID
        const lastIdx = props.project.tags.lastIndexOf(tagName);
        if (lastIdx !== -1) {
            props.project.tags.splice(lastIdx, 1, newTag.id);
        } else if (!props.project.tags.includes(newTag.id)) {
            props.project.tags.push(newTag.id);
        }

        // Update metadata store with new tag
        if (typeof state.addTag === "function") {
            state.addTag(newTag);
        } else {
            // Fallback: manually add to metadata
            if (!state.metadata.tags) {
                state.metadata.tags = [];
            }
            const existingTag = state.metadata.tags.find(
                (t) => t.id === newTag.id
            );
            if (!existingTag) {
                state.metadata.tags.push(newTag);
            }
        }

        return newTag;
    } catch (error) {
        console.error("Error creating tag:", error);
        throw error;
    }
};

function handleTagChange(val) {
    if (val.length > 10) {
        val.pop();
        tagLimitError.value =
            "Maximum 10 tags allowed. Only the first 10 tags have been kept.";
    }
}

onMounted(async () => {
    // Ensure metadata is loaded first
    if (!state.loaded) {
        await state.fetchData();
    }

    // Initialize tags array if it doesn't exist
    if (!props.project.tags) {
        props.project.tags = [];
    }
});
</script>
